import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { useNavigate } from 'react-router-dom'
import type { AktivDetail } from '../../hooks/instrumentRegeln'
import { Loading } from '../common/Feedback'
import s from './AktivitaetDetail.module.scss'

/**
 * Die Schublade rechts: das Detail zu einer Zeile der Aktivitätskarten.
 *
 * Kopf mit Art und Titel, die Kante oben in der Farbe der Zeile; darunter
 * die Felder, der Text und, wenn es einen gibt, der Verlauf. Unten der
 * Sprung auf die Seite dahinter. Klick daneben oder Escape schließt.
 *
 * AM `body`, wie das Popover: fixed über allem, ohne einen Stapelkontext
 * der Seite zu erben.
 */
export function AktivitaetDetail({ detail, laedt, onZu }: {
  detail: AktivDetail
  /** Das Voll-Detail ist noch unterwegs — Text und Verlauf kommen nach. */
  laedt?: boolean
  onZu: () => void
}) {
  const navigate = useNavigate()

  useEffect(() => {
    function onKey(event: KeyboardEvent) { if (event.key === 'Escape') onZu() }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onZu])

  return createPortal(
    <>
      <div className={s.schleier} onClick={onZu} />
      <div className={s.schublade} role="dialog" aria-label={detail.titel}>
        <div className={s.kopf} data-ton={detail.ton}>
          <div className={s.titel}>
            <div className={s.typ}>{detail.typ}</div>
            <div className={s.name}>{detail.titel}</div>
          </div>
          <button type="button" className={s.schliessen} aria-label="Schließen" onClick={onZu}>✕</button>
        </div>
        <div className={s.inhalt}>
          <div className={s.felder}>
            {detail.felder.map((f) => (
              <span key={f.k} className={s.feld}>
                <span className={s.k}>{f.k}</span>
                <span className={s.v} data-ton={f.ton}>{f.v}</span>
              </span>
            ))}
          </div>
          {laedt && <Loading>lädt das Detail …</Loading>}
          {detail.text && <div className={s.text}>{detail.text}</div>}
          {!laedt && !detail.text && <div className={s.ohneText}>Kein Text hinterlegt.</div>}
          {detail.verlauf.length > 0 && (
            <>
              <div className={s.verlaufTitel}>{detail.art === 'news' ? 'QUELLEN' : 'VERLAUF'}</div>
              <div className={s.verlauf}>
                {detail.verlauf.map((v, i) => (
                  <div key={i} className={s.schritt}>
                    <span className={s.datum}>{v.datum}</span>
                    <span>{v.text}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
        <div className={s.fuss}>
          <button type="button" className={s.oeffnen} onClick={() => navigate(detail.oeffnen.to)}>
            {detail.oeffnen.text}
          </button>
        </div>
      </div>
    </>,
    document.body,
  )
}
